import React from 'react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  avatarSizeClasses,
  AvatarWithFallback,
} from './users/AvatarWithFallback';
import { WatchStatus } from '@/types/application/WatcherDTO';

const getRingColor = (user: User) => {
  if (user.watchKind === 'assigned') {
    return 'ring-blue-500';
  }
  return 'ring-background';
};
interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string; // Optional avatar URL
  watchKind?: WatchStatus; // Optional watch kind for conditional styling
}

export interface MiniAvatarListProps {
  users: User[];
  maxVisible?: number; // Maximum number of avatars to show before "+X more"
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}
export const MiniAvatarList: React.FC<MiniAvatarListProps> = ({
  users,
  maxVisible = 5,
  size = 'md',
  className,
}) => {
  const visibleUsers = users.slice(0, maxVisible);
  const remainingCount = users.length - maxVisible;

  return (
    <div className={cn('flex items-center -space-x-2', className)}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex flex-row relative hover:z-10 transition-transform hover:scale-110 -space-x-2">
            {visibleUsers.map((user) => (
              <AvatarWithFallback
                key={user.id}
                user={user}
                size={size}
                className={cn('shadow-sm ring-2', getRingColor(user))}
              />
            ))}
            {remainingCount > 0 && (
              <Avatar
                className={cn(
                  avatarSizeClasses[size],
                  'shadow-sm ring-2 ring-background',
                )}
              >
                <AvatarFallback className="text-xs font-medium bg-muted">
                  +{remainingCount}
                </AvatarFallback>
              </Avatar>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top">
          <div className="relative z-50">
            <p className="font-medium p-1">
              {users
                .map((user) =>
                  user.watchKind === 'assigned'
                    ? `${user.name} (Assigned)`
                    : user.name,
                )
                .join(', ')}
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </div>
  );
};
